import { ACCEPT_ALL_PREFERENCES } from '../../components/consent-modal/preferences';
import { MiddlewareFunction, Preferences, AbstractAnalyticalTool, AnalyticsTools } from '../../components/consent-modal/types';
import { isAnalyticsConsentGranted, setAnalyticsConsent } from './analyticsConsent';
import { appVersionMiddleware, sanitizedPropertiesMiddleware, userContextMiddleware } from './middleware';
import { ALL_TOOLS, BEHAVIORAL_TOOLS } from './tools';

export class AnalyticsWithMiddleware {
	private middlewares: MiddlewareFunction[] = [];
	private isLoaded = false;

	constructor() {
		this.addSourceMiddleware(appVersionMiddleware);
		this.addSourceMiddleware(sanitizedPropertiesMiddleware);
		this.addSourceMiddleware(userContextMiddleware);
	}

	load(userPreferances: Preferences = ACCEPT_ALL_PREFERENCES) {
		ALL_TOOLS.forEach(t => {
			t.load(userPreferances);
		});

		this.isLoaded = true;
		setAnalyticsConsent(isAnalyticsConsentGranted(userPreferances));
	}

	addSourceMiddleware(middleware: MiddlewareFunction) {
		this.middlewares.push(middleware);
	}

	removeSourceMiddleware(middleware: MiddlewareFunction) {
		this.middlewares = this.middlewares.filter((m) => m !== middleware);
	}

	private applyMiddlewares(payload: any, next: (payload: any) => void) {
		const [firstMiddleware, ...rest] = this.middlewares;

		const chain = rest.reduceRight(
			(nextMiddleware, currentMiddleware) => {
				return (p) => currentMiddleware({ payload: p, next: nextMiddleware });
			},
			next
		);

		firstMiddleware({ payload, next: chain });
	}

	private executeWithMiddleware<T extends AbstractAnalyticalTool, P>(
		tools: T[],
		payload: P,
		callback: (tool: T, finalPayload: P) => void
	) {
		for (const tool of tools) {
			if (tool?.supportsMiddleware) {
				this.applyMiddlewares(payload, (finalPayload) => {
					callback(tool, finalPayload);
				});
			}
			else {
				callback(tool, payload);
			}
		}
	}

	track(event: string, properties: any = {}) {
		if (!this.isLoaded) {
			return;
		}
		const payload = { obj: { event, properties } };

		this.executeWithMiddleware(
			BEHAVIORAL_TOOLS,
			payload,
			(tool, finalPayload) => tool.track(finalPayload.obj.event, finalPayload.obj.properties)
		);
	}

	identify(userId: string, traits: any = {}) {
		if (!this.isLoaded) {
			return;
		}
		const payload = { obj: { userId, traits } };

		this.executeWithMiddleware(
			ALL_TOOLS,
			payload,
			(tool, finalPayload) => tool.identify(finalPayload.obj.userId, finalPayload.obj.traits)
		);
	}

	page(name: string, properties: any = {}) {
		if (!this.isLoaded) {
			return;
		}
		const payload = { obj: { name, properties } };

		this.executeWithMiddleware(
			BEHAVIORAL_TOOLS,
			payload,
			(tool, finalPayload) => tool.track('Loaded a Page', finalPayload.obj.properties)
		);
	}

	getClient(AnalyticsTool: AnalyticsTools): AbstractAnalyticalTool | undefined {
		return BEHAVIORAL_TOOLS.find(t => t.key === AnalyticsTool);
	}
}

const analytics = new AnalyticsWithMiddleware();

export { analytics };
